import { Access } from 'src/app/interfaces/api/access';
import { Model } from "./model";
import { User } from "./user";

export type MediaServer = "prod" | "dev";

export interface ProjectPreview {
    database: string;
    image: string;
}
export interface ProjectStatus {
    database: string;
    // status: string;
    last: string;
    communication: number;
    unitConnected: number;
    unitCount: number;
}
export interface EnableTag {
    name: string;
    module: string;
    enabled: boolean;
    // Empty means the label's own displayName applies.
    displayName: string;
    hidden?: boolean;
}

interface DaySchedule {
    enable: boolean;
    times: [string, string][];
}

export interface Schedule {
    mon: DaySchedule;
    tue: DaySchedule;
    wed: DaySchedule;
    thu: DaySchedule;
    fri: DaySchedule;
    sat: DaySchedule;
    sun: DaySchedule;
}

export type NormalizedDay = {
    start: string[];
    end: string[];
};

export type OldDaySchedule = [string, string] | [];

export type OldSchedule = {
    mon: OldDaySchedule;
    tue: OldDaySchedule;
    wed: OldDaySchedule;
    thu: OldDaySchedule;
    fri: OldDaySchedule;
    sat: OldDaySchedule;
    sun: OldDaySchedule;
};

export const DEFAULT_SCHEDULE: Schedule = {
    mon: { enable: true, times: [['00:00', '23:59']] },
    tue: { enable: true, times: [['00:00', '23:59']] },
    wed: { enable: true, times: [['00:00', '23:59']] },
    thu: { enable: true, times: [['00:00', '23:59']] },
    fri: { enable: true, times: [['00:00', '23:59']] },
    sat: { enable: false, times: [['00:00', '23:59']] },
    sun: { enable: false, times: [['00:00', '23:59']] },
};

export type CompatibleSchedule = Schedule | OldSchedule;

export type NormalizedSchedule = {
    [key in keyof Schedule]: NormalizedDay;
};


export interface MetricModel {
    _id?: string;
    name?: string;
    payload?: {
        label?: string;
        value?: string;
        options?: any[];
        type?: "text" | "number" | "date" | "select" | "checkbox" | "textarea";
        isDefault?: boolean;
        widthDiv?: number;
    }[];
}

export interface StreamCredit {
    date: string | number; // ISO date string for the day this credit applies to
    room?: string;
    broadcaster?: string;
    played: number;        // seconds of stream playback used that day
    total: number;         // seconds of playback permitted that day
}

export interface Project {
    id: string;
    _id?: string;
    owner?: User | string;
    author: User | string;
    users?: (User | string)[];
    guests?: (User | string)[];
    privacy: "private" | "user" | "public";
    timezone?: string;
    locale?: string;
    utc: string;
    city?: string;
    street?: string;
    country?: string;
    zipCode?: string;
    description?: string;
    logo?: string;
    picture?: string;
    lat?: number;
    lon?: number;
    type: string;
    startingPage: string;
    mailingList?: (User | string)[];
    model?: Model | string;
    publicAccessKey?: string;
    secretAccessKey?: string;
    accessKey?: string;
    flowAccessKey?: string;
    color: {
        danger: string;
        warning: string;
        primary: string;
        success: string;
        info: string;
        dark: string;
        muted: string;
    };
    createdAt: string | number;
    createdDate: string | number;
    endDate: string | number;
    startDate: string | number;
    eventTypes?: string[];
    name: string;
    company?: string;
    password?: string;
    code?: string;
    database: string;
    offlineDelay: number;
    archived: boolean;
    frozen?: boolean;
    predictionTags?: string[];
    reportSettings: {
        daily: boolean;
        weekly: boolean;
        timeline: boolean;
        eventList: boolean;
        comCheck: boolean;
        charts: boolean;
    };
    schedule: Schedule;
    showOfflineTrackers: boolean;
    reference?: string;
    services: {
        prediction?: string;
        database?: string;
        flowRealtime?: string;
        flowPort?: string;
        mediaServer?: MediaServer;
    };
    transfer?: {
        status: string;
        enabled: boolean;
        messages?: string[];
    };
    timelines?: Record<string, any>[];
    showDisconnectTrackers: boolean;
    riskFilters?: {
        risk?: any[];
        criticity?: any[];
        zone?: any[];
        sequence?: any[];
    };
    enableTags?: EnableTag[];
    reportsEnabled: boolean;
    dashboardOrder?: string[];
    nelia: NeliaOpts;
    enlaps: {
        enabled: boolean;
        url: string;
    };
    portNtrip?: number;
    tabCustomise: string[];
    datavizTabs?: string[];
    settingTabs?: string[];
    singleTimeline: boolean;
    metricModels?: MetricModel[];
    geolocation: {
        accuracy: number;
        duration: number;
    };
    //front use
    thumbnail?: string;
    loading?: boolean;
    favourite?: boolean;
    status?: ProjectStatus;
    activity?: string;
    order?: number;
    modality?: String;
    iaModels?: {
        llm: boolean;
        crane: boolean;
        worker: boolean;
        risk: boolean;
        action: boolean;
        zone: boolean;
    };
    pixelSize?: number;
    selected?: boolean;
    access?: boolean;
    accesses?: Access[];
    active?: boolean;
    lastUpdate?: string;
    aiModules?: Record<string, { enabled: boolean, iteration: string }>;
    addDemoData?: boolean;
    demoData?: ServiceData[];
    serviceConfigs?: Record<string, ServiceConfig>;
    reportParams?: { description: boolean; zone: boolean; sequence: boolean };
    streamDailyLimit?: number;       // seconds of dashboard playback permitted per day (0/undefined = unlimited)
    streamCredits?: StreamCredit[];  // per-day playback usage history
}

export interface PostProject {
    startDate: string | number;
    endDate: string | number;
    name: string;
    author?: User | string;
    accessKey?: string;
    flowAccessKey?: string;
    publicAccessKey?: string;
    secretAccessKey?: string;
    company?: string;
    owner?: User | string;
    users?: (User | string)[];
    guests?: (User | string)[];
    privacy: "private" | "user" | "public";
    timezone?: string;
    locale?: string;
    utc: string;
    city?: string;
    street?: string;
    country?: string;
    zipCode?: string;
    description?: string;
    picture?: string;
    lat?: number;
    lon?: number;
    type: string;
    startingPage: string;
    mailingList?: (User | string)[];
    model?: Model | string;
    color: {
        danger: string;
        warning: string;
        primary: string;
        success: string;
        info: string;
        dark: string;
        muted: string;
    };
    createdAt?: string | number;
    eventTypes?: string[];
    password?: string;
    code?: string;
    database?: string;
    offlineDelay: number;
    archived: boolean;
    predictionTags?: string[];
    reportSettings?: {
        daily: boolean;
        weekly: boolean;
        timeline: boolean;
        eventList: boolean;
        comCheck: boolean;
        charts: boolean;
    };
    schedule?: {
        mon: { enable: boolean; times: string[][] };
        tue: { enable: boolean; times: string[][] };
        wed: { enable: boolean; times: string[][] };
        thu: { enable: boolean; times: string[][] };
        fri: { enable: boolean; times: string[][] };
        sat: { enable: boolean; times: string[][] };
        sun: { enable: boolean; times: string[][] };
    };
    showOfflineTrackers?: boolean;
    reference?: string;
    services?: {
        prediction?: string;
        database?: string;
        flowRealtime?: string;
        flowPort?: string;
        mediaServer?: MediaServer;
    };
    transfer?: {
        status: string;
        enabled: boolean;
        messages?: string[];
    };
    timelapseGo?: {
        box?: number;
        token?: string;
    };
    timelines?: Record<string, any>[];
    showDisconnectTrackers?: boolean;
    riskFilters?: {
        risk?: any[];
        criticity?: any[];
        zone?: any[];
        sequence?: any[];
    };
    enableTags?: EnableTag[];
    reportsEnabled: boolean;
    dashboardOrder?: string[];
    nelia?: {
        enabled: boolean;
        reportTime: string;
        trackers?: string[];
    };
    enlaps?: {
        enabled: boolean;
        trackers?: string[];
    };
    portNtrip?: number;
    tabCustomise: string[];
    datavizTabs?: string[];
    settingTabs?: string[];
    singleTimeline: boolean;
    metricModels?: MetricModel[];
    geolocation: {
        accuracy: number;
        duration: number;
    };
    iaModels?: {
        llm: boolean;
        crane: boolean;
        worker: boolean;
        risk: boolean;
        action: boolean;
        zone: boolean;
    };
    pixelSize?: number;
    streamDailyLimit?: number;
    streamCredits?: StreamCredit[];
}

export interface PutProject {
    id: string;
    startDate?: string | number;
    endDate?: string | number;
    name?: string;
    author?: User | string;
    accessKey?: string;
    flowAccessKey?: string;
    publicAccessKey?: string;
    secretAccessKey?: string;
    company?: string;
    owner?: User | string;
    users?: (User | string)[];
    guests?: (User | string)[];
    privacy?: "private" | "user" | "public";
    timezone?: string;
    locale?: string;
    utc?: string;
    city?: string;
    street?: string;
    country?: string;
    zipCode?: string;
    description?: string;
    picture?: string;
    logo?: string;
    lat?: number;
    lon?: number;
    type?: string;
    startingPage?: string;
    mailingList?: (User | string)[];
    model?: Model | string;
    createdAt?: string | number;
    eventTypes?: string[];
    password?: string;
    code?: string;
    database?: string;
    offlineDelay?: number;
    archived?: boolean;
    predictionTags?: string[];
    reportSettings?: {
        daily: boolean;
        weekly: boolean;
        timeline: boolean;
        eventList: boolean;
        comCheck: boolean;
        charts: boolean;
    };
    schedule?: {
        mon: { enable: boolean; times: string[][] };
        tue: { enable: boolean; times: string[][] };
        wed: { enable: boolean; times: string[][] };
        thu: { enable: boolean; times: string[][] };
        fri: { enable: boolean; times: string[][] };
        sat: { enable: boolean; times: string[][] };
        sun: { enable: boolean; times: string[][] };
    };
    showOfflineTrackers?: boolean;
    reference?: string;
    services?: {
        prediction?: string;
        database?: string;
        flowRealtime?: string;
        flowPort?: string;
        mediaServer?: MediaServer;
    };
    transfer?: {
        status: string;
        enabled: boolean;
        messages?: string[];
    };
    timelines?: Record<string, any>;
    showDisconnectTrackers?: boolean;
    riskFilters?: {
        risk?: any[];
        criticity?: any[];
        zone?: any[];
        sequence?: any[];
    };
    enableTags?: EnableTag[];
    reportsEnabled?: boolean;
    dashboardOrder?: string[];
    nelia?: NeliaOpts;
    enlaps?: {
        enabled: boolean;
        url: string;
    };
    portNtrip?: number;
    tabCustomise?: string[];
    datavizTabs?: string[];
    settingTabs?: string[];
    singleTimeline?: boolean;
    metricModels?: MetricModel[];
    geolocation?: {
        accuracy: number;
        duration: number;
    };
    iaModels?: any;
    pixelSize?: number;
    aiModules?: Record<string, { enabled: boolean, iteration: string }>;
    serviceConfigs?: Record<string, ServiceConfig>;
    reportParams?: { description: boolean; zone: boolean; sequence: boolean };
    streamDailyLimit?: number;
    streamCredits?: StreamCredit[];
}

export interface NeliaOpts {
    enabled: boolean;
    realtime: boolean;
    reportTime: string;
    trackers?: string[];
}

export const defaultProject: Project = {
    id: "",
    name: "",
    author: "",
    privacy: "private",
    utc: "",
    type: "",
    startingPage: "",
    color: {
        danger: "",
        warning: "",
        primary: "",
        success: "",
        info: "",
        dark: "",
        muted: ""
    },
    createdAt: "",
    createdDate: "",
    endDate: "",
    startDate: "",
    database: "",
    offlineDelay: 0,
    archived: false,
    reportSettings: {
        daily: false,
        weekly: false,
        timeline: false,
        eventList: false,
        comCheck: false,
        charts: false
    },
    schedule: {
        mon: { enable: false, times: [] },
        tue: { enable: false, times: [] },
        wed: { enable: false, times: [] },
        thu: { enable: false, times: [] },
        fri: { enable: false, times: [] },
        sat: { enable: false, times: [] },
        sun: { enable: false, times: [] }
    },
    showOfflineTrackers: false,
    showDisconnectTrackers: false,
    reportsEnabled: false,
    tabCustomise: [],
    datavizTabs: [],
    settingTabs: [],
    singleTimeline: false,
    geolocation: {
        accuracy: 0,
        duration: 0
    },
    services: {},
    nelia: {
        enabled: false,
        realtime: false,
        reportTime: "18:00",
        trackers: []
    },
    enlaps: {
        enabled: false,
        url: ""
    },
    aiModules: {
        crane: { enabled: false, iteration: "" },
        risk: { enabled: false, iteration: "" },
        worker: { enabled: false, iteration: "" },
        action: { enabled: false, iteration: "" },
        zone: { enabled: false, iteration: "" }
    },
    pixelSize: 1,
    addDemoData: false,
    demoData: [],
    streamCredits: []
};

export interface ServiceData {
    database: string;
    dashboards: string[];
    units: string[];
    flows?: string[];
    unitQuantities?: Record<string, number>;
    trackers?: string[];
    zones?: string[];
    startDate?: string;
    endDate?: string;
}

export interface ServiceConfig {
    name?: string;
    projectType: string[];
    datavizTabs: string[];
    settingTabs: string[];
    predictions: string[];
    data: ServiceData[];
    riskEnabled?: boolean;
    icon?: string;
    color?: string;
}

export const serviceConfigs: Record<string, ServiceConfig> = {};
